import { Check, Moon, Sun } from "lucide-react";
import { PALETTES } from "@/app/palettes.generated";
import { cn } from "@/lib/cn";
import { useTheme, type Theme } from "@/theme/ThemeProvider";

const OPTIONS: { value: Theme; label: string; description: string; icon: typeof Sun }[] = [
  { value: "dark", label: "Dark", description: "The default. Deep violet-grey surfaces.", icon: Moon },
  { value: "light", label: "Light", description: "The same interface on a pale canvas.", icon: Sun },
];

/**
 * Settings → Themes (UI-17).
 *
 * Choosing a theme flips one class on the document root and every token re-resolves.
 * Nothing here knows what any component looks like in either mode.
 */
export function ThemePicker({ summary }: { summary: string }) {
  const { theme, setTheme } = useTheme();

  return (
    <div className="px-[var(--space-8)] py-[var(--space-8)]">
      <header className="mb-[var(--space-6)]">
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Themes
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      <div className="grid grid-cols-2 gap-[var(--space-4)]">
        {OPTIONS.map((option) => {
          const active = theme === option.value;
          const Icon = option.icon;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setTheme(option.value)}
              aria-pressed={active}
              className={cn(
                "flex flex-col items-start gap-[var(--space-2)] rounded-[var(--radius-xl)] border p-[var(--space-4)] text-left transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--primary)] bg-[var(--surface-glass)]"
                  : "border-[var(--border-subtle)] hover:border-[var(--border-default)] hover:bg-[var(--surface-glass)]",
              )}
            >
              <span className="flex w-full items-center gap-[var(--space-2)]">
                <Icon size={16} strokeWidth={2} className="text-[var(--text-secondary)]" />
                <span className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
                  {option.label}
                </span>
                {active && (
                  <span
                    className="ml-auto flex items-center gap-1 rounded-[var(--radius-full)] px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold"
                    style={{
                      background: "color-mix(in oklch, var(--primary) 20%, transparent)",
                      color: "var(--primary)",
                    }}
                  >
                    <Check size={10} strokeWidth={3} /> On
                  </span>
                )}
              </span>
              <span className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">{option.description}</span>
            </button>
          );
        })}
      </div>

      <p className="mt-[var(--space-3)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
        Your choice is remembered on this device and saved to your account, so it follows you to another one.
      </p>

      <PalettePicker />
    </div>
  );
}

/**
 * Palettes recolour the app without touching its typography, spacing or layout.
 *
 * Factorium is the default and stays the source of truth (UI-2) — it is the ported
 * design handoff, and choosing nothing means using it. The rest are converted from the
 * theme files in `themes/`, which define only the shadcn layer; the surface scale, text
 * colours and brand accents are derived from that, so they are coherent rather than
 * designed. Each one has its own dark and light form, so this composes with the choice
 * above rather than replacing it.
 */
function PalettePicker() {
  const { palette, setPalette } = useTheme();

  return (
    <section className="mt-[var(--space-12)]">
      <h2 className="text-[length:var(--text-2xl)] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
        Palette
      </h2>
      <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">
        Recolours the app. Dark and light both keep working, whichever you pick.
      </p>

      <div className="mt-[var(--space-4)] grid grid-cols-3 gap-[var(--space-3)]">
        <PaletteCard
          id={null}
          label="Factorium"
          swatch="var(--gradient-brand)"
          note="The design handoff"
          active={palette === null}
          onSelect={setPalette}
        />

        {PALETTES.map((option) => (
          <PaletteCard
            key={option.id}
            id={option.id}
            label={option.label}
            swatch={option.swatch}
            active={palette === option.id}
            onSelect={setPalette}
          />
        ))}
      </div>
    </section>
  );
}

function PaletteCard({
  id,
  label,
  swatch,
  note,
  active,
  onSelect,
}: {
  id: string | null;
  label: string;
  swatch: string;
  note?: string;
  active: boolean;
  onSelect: (palette: string | null) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(id)}
      aria-pressed={active}
      className={cn(
        "flex items-center gap-[var(--space-2)] rounded-[var(--radius-lg)] border p-[var(--space-3)] text-left transition-colors duration-[var(--duration-fast)]",
        active
          ? "border-[var(--primary)] bg-[var(--surface-glass)]"
          : "border-[var(--border-subtle)] hover:border-[var(--border-default)] hover:bg-[var(--surface-glass)]",
      )}
    >
      <span
        aria-hidden
        className="h-6 w-6 shrink-0 rounded-[var(--radius-full)] border border-[var(--border-subtle)]"
        style={{ background: swatch }}
      />
      <span className="min-w-0">
        <span className="block truncate text-[length:var(--text-sm)] font-medium text-[var(--text-primary)]">
          {label}
        </span>
        {note && <span className="block truncate text-[length:var(--text-2xs)] text-[var(--text-muted)]">{note}</span>}
      </span>
      {active && <Check size={13} strokeWidth={3} className="ml-auto shrink-0 text-[var(--primary)]" />}
    </button>
  );
}
